/**
 * Recognising a failure caused by the firm's PMS connection having lapsed.
 *
 * The backend answers these with **424 Failed Dependency** and the code below —
 * never 401, because `AuthGate` reads a 401 as the user's own Vale session dying
 * and responds by signing them out entirely. That is exactly the bug VALE-281
 * fixed: an expired integration tore down the session on every PMS-backed
 * action. Anything here that starts treating this failure as an auth failure
 * reintroduces it.
 *
 * Callers key on the **code**, never on the message text: the wire message is
 * deliberately provider-neutral, and the brand name belongs to
 * `getPmsProviderPresentation`, this app's single source of truth for it.
 */

'use client';

import { DirectApiError } from '@/lib/api/direct-client';

/**
 * Must stay in sync with `INTEGRATION_REAUTH_REQUIRED_CODE` in
 * `backend/src/customer-integrations/core/errors/integration-auth.error.ts`.
 */
export const INTEGRATION_REAUTH_REQUIRED_CODE = 'INTEGRATION_REAUTH_REQUIRED';

/**
 * Whether a thrown error is the backend saying the firm's PMS connection needs
 * reconnecting before this action can work.
 */
export function isIntegrationReauthRequiredError(error: unknown): boolean {
  return (
    error instanceof DirectApiError &&
    error.response?.code === INTEGRATION_REAUTH_REQUIRED_CODE
  );
}
